'use client';
import dynamic from 'next/dynamic';
import { IProductContext, ProductContext } from './ProductContext';
import { Container } from '@/src/packages/components/ui/Container';
import { Box } from '@/src/packages/components/ui/Box';
import { ProductImage } from '@/src/packages/components/pages/Product/ProductImage';
import { ProductControls } from '@/src/packages/components/pages/Product/ProductControls';
import { ProductDetails } from '@/src/packages/components/pages/Product/ProductDetails';
import { PageSectionStack } from '@/src/packages/components/templates/PageSectionStack';
import { ProductBreadcrumb } from '@/src/packages/components/pages/Product/ProductBreadcrumb';
import { ProductSketch } from '@/src/packages/components/pages/Product/ProductSketch';
import { ProductFormContext } from '@/src/packages/components/contexts/product-form.context';
import { ProductBundles } from '@/src/packages/components/pages/Product/ProductBundles';
import { PageWrapper } from '@/src/packages/components/templates/PageWrapper';
import Image from 'next/image';
import { useInversedNav } from '@/src/packages/components/ui/Navigation';

const ProductMaterials = dynamic(() =>
  import('@/src/packages/components/pages/Product/ProductMaterials').then(
    (mod) => mod.ProductMaterials,
  ),
);

const ProductCompleteTheLook = dynamic(() =>
  import('@/src/packages/components/pages/Product/ProductCompleteTheLook').then(
    (mod) => mod.ProductCompleteTheLook,
  ),
);

const ProductHighlights = dynamic(() =>
  import('@/src/packages/components/pages/Product/ProductHighlights').then(
    (mod) => mod.ProductHighlights,
  ),
);

const ProductLookbook = dynamic(() =>
  import('@/src/packages/components/pages/Product/ProductLookbook').then(
    (mod) => mod.ProductLookbook,
  ),
);

const ProductManufacturingProcess = dynamic(() =>
  import(
    '@/src/packages/components/pages/Product/ProductManufacturingProcess'
  ).then((mod) => mod.ProductManufacturingProcess),
);

const ProductUpsellingModal = dynamic(() =>
  import('@/src/packages/components/pages/Product/ProductUpsellingModal').then(
    (mod) => mod.ProductUpsellingModal,
  ),
);

const ProductReviews = dynamic(() =>
  import('@/src/packages/components/pages/Product/ProductReviews').then(
    (mod) => mod.ProductReviews,
  ),
);

export type IProductPage = Pick<
  IProductContext,
  'productVariant' | 'reviews' | 'selectedVariant'
>;

export const ProductPage = (props: IProductPage) => {
  const options = props.productVariant.product.options.filter(
    (option) => !option.isVariant,
  );

  const ctx: IProductContext = {
    ...props,
    options,
  };

  useInversedNav();

  return (
    <ProductFormContext options={options} skus={props.productVariant.skus}>
      <ProductContext value={ctx}>
        <PageWrapper paddingTop={0}>
          {props.productVariant.cover && (
            <Box className="bg-background-secondary relative -mt-[var(--navigation-height)] h-[calc(100vh-var(--navigation-announcement-height))]">
              <Image
                src={props.productVariant.cover?.fields.file.url}
                fill
                priority
                alt={props.productVariant.title}
                sizes="100vw"
                className="object-cover"
              />
            </Box>
          )}
          <Container>
            <ProductBreadcrumb />
            <ProductUpsellingModal />
            <PageSectionStack>
              <Box
                className="grid grid-cols-12 gap-4 lg:gap-0"
                style={{
                  scrollMarginTop:
                    'calc(var(--navigation-height) + var(--navigation-announcement-height))',
                }}
              >
                <Box colSpan={{ initial: 12, lg: 7 }}>
                  <ProductImage />
                </Box>
                <Box colSpan={{ initial: 12, lg: 5 }}>
                  <Box className="top-gutter sticky mx-auto space-y-4 lg:max-w-lg lg:px-4">
                    <ProductControls />
                    <ProductDetails />
                    <ProductBundles />
                  </Box>
                </Box>
              </Box>
              <ProductSketch />
              <ProductHighlights />
              {props.productVariant.manufacturingProcess && (
                <ProductManufacturingProcess
                  manufacturingProcess={
                    props.productVariant.manufacturingProcess
                  }
                />
              )}
              <ProductMaterials />
              <ProductLookbook
                images={props.productVariant.product.gallery || []}
              />
              <ProductReviews reviews={props.reviews} />
              <ProductCompleteTheLook />
              {/* <ProductWarranty /> */}
            </PageSectionStack>
          </Container>
        </PageWrapper>
      </ProductContext>
    </ProductFormContext>
  );
};
